import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

export type LoadingSpinnerProps = {
  className?: string;
  color?: 'accent' | 'secondary' | 'tertiary' | 'primary' | 'quinary' | 'translucid';
  ariaLabel?: string;
  size?: 'compact' | 'default' | 'large' | 'xlarge';
  animate?: boolean;
};

const LoadingSpinner = ({
  className,
  color = 'primary',
  ariaLabel,
  size = 'default',
  animate = true,
}: LoadingSpinnerProps) => {
  const { t } = useTranslation('ui', {
    keyPrefix: 'components.loading-indicator',
  });
  const getTrackStroke = () => {
    if (color === 'accent') return 'var(--loading-spinner-accent-track)';
    if (color === 'quinary') return 'var(--loading-spinner-quinary-track)';
    return 'var(--border-color-tertiary)';
  };

  return (
    <div
      className={cx(
        'flex items-center justify-center',
        {
          'text-accent': color === 'accent',
          'text-secondary': color === 'secondary',
          'text-tertiary': color === 'tertiary',
          'text-primary': color === 'primary' || color === 'translucid',
          'text-quinary': color === 'quinary',
        },
        {
          'w-3 h-3': size === 'compact',
          'w-4 h-4': size === 'default',
          'w-5 h-5': size === 'large',
          'w-6 h-6': size === 'xlarge',
        },
        className,
      )}
      // @ts-ignore: fails for dashboard project due to i18next selector
      aria-label={ariaLabel || t('aria-label-default')}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={50}
    >
      <svg
        className={cx('w-full h-full', animate && 'animate-spin motion-reduce:animate-none')}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx={16}
          cy={16}
          r={12}
          strokeWidth={4}
          fill="none"
          stroke={getTrackStroke()}
          strokeOpacity={color === 'translucid' ? 0.15 : 1}
        />
        <circle
          className={cx(
            'origin-center [transform-box:fill-box]',
            animate && 'animate-spinner-dash motion-reduce:animate-none',
          )}
          cx={16}
          cy={16}
          r={12}
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
          fill="none"
          strokeDasharray="75 75"
          strokeOpacity={color === 'translucid' ? 0.3 : 1}
        />
      </svg>
    </div>
  );
};

export default LoadingSpinner;
